import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { useRegisterPost } from "@/api/AppApi/services/auth";
import { IRegisterParams } from "@/api/AppApi/services/auth/interfaces/IAuth";
import { useGetRegisterFieldsQuery } from "@/api/AppApi/services/builder";
import { IField } from "@/api/AppApi/services/builder/interfaces/interfaces";
import { LoginMethodTypes } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import AuthButton from "../auth-common/AuthButton";
import {
  deleteTokens,
  storeAuthTokens,
} from "@/utils/functions/cookieOperations";
import OtherFields from "./other-field/OtherFields";
import RegisterMainFields from "./RegisterMainFields";
import { useAtom } from "jotai";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { accountConfirmQuery } from "./register-confirm/AccountConfirm";
import {
  adaptFieldsToPost,
  defaultValues,
  formErrors,
  renameFields,
} from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import NotFieldErrModal from "@/app/components/common-form-builder/form-builder-helpers/NotFieldErrModal";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useSearchParams } from "next/navigation";
import { PagesPaths } from "@/utils/functions/pages-paths";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import {
  EventType,
  IGTagUserData,
} from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import { getFbcCookie, getFbpCookie } from "@/utils/functions/cookieOperations";
import { v4 as uuidv4 } from "uuid";

interface Props {
  registerBy?: LoginMethodTypes;
  reference_id?: number;
  className: string;
  isFromCheckout?: boolean;
}
const RegisterForm: React.FC<Props> = ({
  registerBy,
  className,
  reference_id,
  isFromCheckout,
}) => {
  const t = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const reactForm = useForm();
  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);
  const { handleSubmit } = reactForm;
  const [, setSignedIn] = useAtom(isSignedInAtom);
  const { userLoginEventTracher, eventTrackers, GTagLeadEventTracher } =
    useEventTrackers();
  const getRegisterFields = useGetRegisterFieldsQuery(reference_id);
  const builder = getRegisterFields?.data?.result?.builder;
  const [fields, setFields] = useState<IField[]>();

  const isFromCart = searchParams.get("fromCart");
  const viewEmail = [LoginMethodTypes.both, LoginMethodTypes.by_email].includes(
    registerBy!,
  );
  const viewPhone = [LoginMethodTypes.both, LoginMethodTypes.by_phone].includes(
    registerBy!,
  );
  const viewBoth = viewEmail && viewPhone;
  const allMainFields = fields?.filter((f) => !f.configurations.is_extra);
  const mainFields = viewBoth
    ? allMainFields
    : viewPhone
    ? allMainFields?.filter((field) => field.configurations.key !== "email")
    : allMainFields?.filter((field) => field.configurations.key !== "mobile");
  const extraFields = fields?.filter((f) => f.configurations.is_extra);
  // const isEmail = registerBy === LoginMethodTypes.by_email;
  const registerPost = useRegisterPost({
    onSuccess: (res) => {
      const { user } = res?.result ?? {};
      if (res?.message?.type === ApiMsgTypes.success) {
        storeAuthTokens(res.result?.access_token!, res.result?.refresh_token!);
        setSignedIn(true);
        const userData: IGTagUserData = {
          user_id: user?.id,
          name: user?.name,
          email: user?.email,
          phone: user?.mobile,
        };
        userLoginEventTracher(true, { ...userData });
        if (
          res?.result?.user?.is_email_verified &&
          res?.result?.user?.is_mobile_verified
        )
          router.replace(
            isFromCheckout
              ? PagesPaths.userCheckout
              : isFromCart
              ? PagesPaths.cart
              : "/",
          );
        else
          router.replace(
            accountConfirmQuery({
              confirm: registerBy!,
              redirectTo: isFromCheckout
                ? PagesPaths.userCheckout
                : isFromCart
                ? PagesPaths.cart
                : "",
            }),
          );
      }
    },
    onError: (error) => {
      const extraErr = formErrors(error, reactForm);
      setNotFieldErr(extraErr);
    },
  });
  const onSubmit = (data: any) => {
    deleteTokens();
    let adaptedFields = adaptFieldsToPost(data);
    if (!viewBoth) {
      if (viewPhone) adaptedFields.email = null;
      else adaptedFields.mobile = null;
    }
    const event_id = uuidv4();
    const fbc = (getFbcCookie() as string) ?? null;
    const fbp = (getFbpCookie() as string) ?? null;
    const leadUserdata: IGTagUserData = {
      name: adaptedFields?.name,
      email: adaptedFields?.email ?? undefined,
      phone: adaptedFields?.mobile ?? undefined,
    };
    GTagLeadEventTracher(leadUserdata);
    eventTrackers(EventType.lead, [], {}, leadUserdata, event_id);
    registerPost.mutate({ body: adaptedFields as IRegisterParams, params: { fbc, fbp, event_id } });
  };
  const handleInnerSubmit = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    handleSubmit(onSubmit)(event);
  };
  useEffect(() => {
    if (getRegisterFields.isLoading) return;
    const fields = renameFields(builder);
    reactForm.reset(defaultValues(fields));
    setFields(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRegisterFields.isLoading]);

  return (
    <>
      {notFieldErr.length > 0 && (
        <NotFieldErrModal errors={notFieldErr} setErrors={setNotFieldErr} />
      )}
      <form onSubmit={handleInnerSubmit} className="w-100">
        <div className={`auth-form  ${className}`}>
          <RegisterMainFields form={reactForm} fields={mainFields ?? []} />
        </div>
        {extraFields && extraFields.length > 0 && (
          <OtherFields
            form={reactForm}
            fields={extraFields}
            className={className}
          />
        )}
        <div className={className}>
          <AuthButton isLoading={registerPost.isLoading}>
            {reference_id ? t.create_account_now : t.register}
          </AuthButton>
        </div>
      </form>
    </>
  );
};

export default RegisterForm;
